import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { workspaceService } from '../services/api/workspace.service';
import { suscripcionesService } from '../services/api/suscripciones.service';
import { storageService } from '../utils/storage';
import { EVENTO_ESTABLECIMIENTO_NO_AUTORIZADO } from '../shared/workspace/eventosWorkspace';
import type { Empresa, Establecimiento } from '../modelos/Auth';

interface WorkspaceContextType {
  empresaId: string | null;
  establecimientoId: string | null;
  empresas: Empresa[];
  establecimientos: Establecimiento[];
  esCargando: boolean;
  error: string | null;
  setEmpresa: (empresaId: string) => void;
  setEstablecimiento: (establecimientoId: string) => void;
  setWorkspace: (empresaId: string, establecimientoId?: string) => void;
  clearWorkspace: () => void;
  hasContext: boolean;
  currentEmpresa: Empresa | null;
  currentEstablecimiento: Establecimiento | null;
  loadEmpresas: () => Promise<void>;
  loadEstablecimientos: (empresaId?: string) => Promise<void>;
  refreshFromStorage: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const corregirEstablecimientoActivo = (
  empresaActualId: string | null,
  autorizadosEnStorage: Establecimiento[],
): string | null => {
  const actualId = workspaceService.getCurrentEstablecimientoId();
  if (!empresaActualId || !actualId) return actualId;

  const autorizados = autorizadosEnStorage.filter(e => e.empresaId === empresaActualId);
  if (autorizados.length === 0 || autorizados.some(e => e.id === actualId)) return actualId;

  const reemplazo = autorizados.find(e => e.esPrincipal) ?? autorizados[0];
  workspaceService.setWorkspace(empresaActualId, reemplazo.id);
  return reemplazo.id;
};

const restringirAEstablecimientoAutorizado = (
  empresaId: string,
  solicitadoId: string | undefined,
): string | undefined => {
  if (!solicitadoId) return solicitadoId;

  const autorizados = storageService.getEstablecimientos().filter(e => e.empresaId === empresaId);
  if (autorizados.length === 0 || autorizados.some(e => e.id === solicitadoId)) return solicitadoId;

  return (autorizados.find(e => e.esPrincipal) ?? autorizados[0]).id;
};

interface WorkspaceProviderProps {
  children: ReactNode;
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const [empresaId, setEmpresaIdState] = useState<string | null>(null);
  const [establecimientoId, setEstablecimientoIdState] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [establecimientos, setEstablecimientos] = useState<Establecimiento[]>([]);
  const [esCargando, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const empresaIdRef = useRef(empresaId);
  useEffect(() => {
    empresaIdRef.current = empresaId;
  }, [empresaId]);

  const loadWorkspaceFromStorage = useCallback(() => {
    const currentEmpresaId = workspaceService.getCurrentEmpresaId();

    // Cargar directamente desde storageService (síncrono)
    const storedEmpresas = storageService.getEmpresas();
    const storedEstablecimientos = storageService.getEstablecimientos();
    const currentEstablecimientoId = corregirEstablecimientoActivo(currentEmpresaId, storedEstablecimientos);

    setEmpresaIdState(currentEmpresaId);
    setEstablecimientoIdState(currentEstablecimientoId);
    setEmpresas(storedEmpresas);
    setEstablecimientos(storedEstablecimientos);
  }, []);

  useEffect(() => {
    loadWorkspaceFromStorage();

    const handleWorkspaceUpdated = () => loadWorkspaceFromStorage();
    window.addEventListener('senciyo:workspace-updated', handleWorkspaceUpdated);
    return () => window.removeEventListener('senciyo:workspace-updated', handleWorkspaceUpdated);
  }, [loadWorkspaceFromStorage]);

  const loadEmpresas = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await workspaceService.getEmpresas();
      const empresasData = Array.isArray(response.data) 
        ? response.data 
        : ((response.data as { data: Empresa[] })?.data || []);
      setEmpresas(empresasData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al cargar empresas');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadEstablecimientos = useCallback(async (filterEmpresaId?: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await workspaceService.getEstablecimientos(filterEmpresaId);
      const fetched = Array.isArray(response.data)
        ? response.data
        : ((response.data as { data: Establecimiento[] })?.data || []);

      const fetchedEmpresaIds = new Set(fetched.map(e => e.empresaId));
      const existing = storageService.getEstablecimientos();
      const others = existing.filter(e => !fetchedEmpresaIds.has(e.empresaId));
      const merged = [...others, ...fetched];

      storageService.updateEstablecimientos(merged);
      setEstablecimientos(merged);
      setEstablecimientoIdState(corregirEstablecimientoActivo(empresaIdRef.current, merged));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al cargar establecimientos');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    void loadEstablecimientos();
  }, [empresaId, loadEstablecimientos]);

  useEffect(() => {
    const handleEstablecimientoNoAutorizado = () => {
      if (empresaIdRef.current) void loadEstablecimientos(empresaIdRef.current);
    };
    window.addEventListener(EVENTO_ESTABLECIMIENTO_NO_AUTORIZADO, handleEstablecimientoNoAutorizado);
    return () => window.removeEventListener(EVENTO_ESTABLECIMIENTO_NO_AUTORIZADO, handleEstablecimientoNoAutorizado);
  }, [loadEstablecimientos]);

  const setEmpresa = useCallback((newEmpresaId: string): void => {
    workspaceService.setWorkspace(newEmpresaId);
    setEmpresaIdState(newEmpresaId);
    void suscripcionesService.cargarLote([newEmpresaId]);
  }, []);

  const setEstablecimiento = useCallback((newEstablecimientoId: string): void => {
    const empresaActual = empresaIdRef.current;
    const permitido = empresaActual
      ? restringirAEstablecimientoAutorizado(empresaActual, newEstablecimientoId)
      : newEstablecimientoId;
    if (empresaActual) {
      workspaceService.setWorkspace(empresaActual, permitido);
    }
    setEstablecimientoIdState(permitido ?? null);
  }, []);

  const setWorkspace = useCallback((newEmpresaId: string, newEstablecimientoId?: string): void => {
    const permitido = restringirAEstablecimientoAutorizado(newEmpresaId, newEstablecimientoId);
    workspaceService.setWorkspace(newEmpresaId, permitido);
    setEmpresaIdState(newEmpresaId);
    setEstablecimientoIdState(permitido || null);
    void suscripcionesService.cargarLote([newEmpresaId]);
  }, []);

  const clearWorkspace = useCallback((): void => {
    workspaceService.clearWorkspace();
    setEmpresaIdState(null);
    setEstablecimientoIdState(null);
  }, []);

  const hasContext = workspaceService.hasWorkspaceContext();
  const currentEmpresa = workspaceService.getCurrentEmpresa();
  const currentEstablecimiento = workspaceService.getCurrentEstablecimiento();

  const value: WorkspaceContextType = {
    empresaId,
    establecimientoId,
    empresas,
    establecimientos,
    esCargando,
    error,
    setEmpresa,
    setEstablecimiento,
    setWorkspace,
    clearWorkspace,
    hasContext,
    currentEmpresa,
    currentEstablecimiento,
    loadEmpresas,
    loadEstablecimientos,
    refreshFromStorage: loadWorkspaceFromStorage,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWorkspace(): WorkspaceContextType {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
